import gql from 'graphql-tag'

/*
  Used for GraphQL Type/Hooks generation and Apollo caching.
*/

export const BOARD_ALERT_DATA = gql`
  fragment boardAlert on PlanAlert {
    alertKey
    alertType
    groupId
    itemId
    level
  }
`

export const BOARD_ITEM_DATA = gql`
  fragment boardItem on PlanItem {
    id
    availability
    campusDescription
    career
    courseNumber
    creditsMax
    creditsMin
    displaySequence
    enrollmentStatus {
      enrolled
      termCode
      termDescription
    }
    equivalentCourseGroupId
    externalCourseId
    gradeReceived
    groupId
    inProgress
    isActive
    isCoursePlaceholder
    locked
    nonCourseCreditsMax
    nonCourseCreditsMin
    nonCourseTitle
    planId
    required
    requirementTitles
    requisiteDescription
    requisiteDescriptionLong
    showRequisiteAlert
    subjectId
    termGradeReceived
    title
    usedToMeetRequirement
    courseTakenInReq
  }
  ${BOARD_ALERT_DATA}
`

export const BOARD_COMMENT_DATA = gql`
  fragment boardComment on Comment {
    id
    comment
    createdByAppUserId
    createdAt
    creatorName
    groupId
    planId
    read
    readTimestamp
  }
`

export const BOARD_MAJOR_DATA = gql`
  fragment boardMajor on PlanMajor {
    id
    planId
    majorCode
    majorDescription
  }
`

export const BOARD_MINOR_DATA = gql`
  fragment boardMinor on PlanMinor {
    id
    planId
    minorCode
    minorDescription
  }
`

export const BOARD_GROUP_DATA = gql`
  fragment boardGroup on PlanGroup {
    id
    comments {
      ...boardComment
    }
    title
    externalTermId
    planId
    displaySequence
    termSequence
    termType
    items {
      ...boardItem
    }
  }
  ${BOARD_ITEM_DATA}
`

export const BOARD_PLAN_DATA = gql`
  fragment boardPlan on Plan {
    id
    catalogYear
    catalogYearDescription
    changelogViewedAt
    campusCode
    campusDescription
    collegeCode
    collegeDescription
    concentrationCode
    concentrationDescription
    createdAt
    creatorName
    degreeCode
    degreeDescription
    description
    groups {
      ...boardGroup
    }
    isFromTemplate
    isPrimary
    isShared
    isTemplate
    majors {
      ...boardMajor
    }
    minors {
      ...boardMinor
    }
    ownerAppUserId
    planViewedAt
    publishedAt
    sourcePlanId
    status
    templateMessage
    title
    updatedAt
    readOnlyForStudents
  }
  ${BOARD_GROUP_DATA}
  ${BOARD_MAJOR_DATA}
  ${BOARD_MINOR_DATA}
`

export const ITEM_MAJOR_DATA = gql`
  fragment itemMajor on ItemMajor {
    id
    itemId
    majorCode
    majorDescription
  }
`

export const ITEM_MINOR_DATA = gql`
  fragment itemMinor on ItemMinor {
    id
    itemId
    minorCode
    minorDescription
  }
`

export const PLACEHOLDER_COURSE_DATA = gql`
  fragment placeholderCourse on PlaceholderCourse {
    campus
    campusDescription
    courseNumber
    creditsMax
    creditsMin
    description
    externalCourseId
    subjectId
    subjectDescription
    title
  }
`

export const PLAN_ITEM_DETAILS = gql`
  fragment planItemDetails on PlanItem {
    id
    availability
    campusCode
    campusDescription
    career
    catalogYear
    catalogYearDescription
    collegeCode
    collegeDescription
    concentrationCode
    concentrationDescription
    courseId
    courseNumber
    creatorName
    creditsMax
    creditsMin
    degreeCode
    degreeDescription
    description
    displaySequence
    enrollmentStatus {
      enrolled
      termCode
      termDescription
    }
    equivalentCourseGroupId
    externalCourseId
    groupId
    inProgress
    isActive
    isCoursePlaceholder
    locked
    majors {
      ...itemMajor
    }
    minors {
      ...itemMinor
    }
    nonCourseCreditsMax
    nonCourseCreditsMin
    nonCourseDescription
    nonCourseTitle
    placeholderCourses {
      id
      ...placeholderCourse
    }
    planId
    publishedAt
    required
    requirementTitles
    requisiteDescription
    requisiteDescriptionLong
    showRequisiteAlert
    status
    subjectDescription
    subjectId
    title
    typicallyOffered
    usedToMeetRequirement
    courseTakenInReq
  }
  ${PLACEHOLDER_COURSE_DATA}
`

export const OWNER_PLAN_DATA = gql`
  fragment OwnerPlan on Plan {
    id
    createdAt
    isFromTemplate
    isPrimary
    isShared
    ownerAppUserId
    planViewedAt
    sourcePlanId
    title
    updatedAt
  }
`

export const CHANGE_LOG_DATA = gql`
  fragment ChangeLog on ChangeLog {
    id
    changedByAppUserId
    changedTimestamp
    description
    planId
    groupId
    itemId
  }
`

export const CHANGE_LOG_EDGE_NODE = gql`
  fragment changeLogEdgeNode on ChangeLogEdge {
    node {
      ...ChangeLog
    }
  }
  ${CHANGE_LOG_DATA}
`

export const CHANGE_LOG_PAGE_INFO = gql`
  fragment changeLogPageInfo on PageInfo {
    endCursor
    hasNextPage
  }
`

export const CHANGE_LOG_CONNECTION = gql`
  fragment changeLogConnection on ChangeLogConnection {
    edges {
      ...changeLogEdgeNode
    }
    pageInfo {
      ...changeLogPageInfo
    }
  }
  ${CHANGE_LOG_EDGE_NODE}
  ${CHANGE_LOG_PAGE_INFO}
`

export const GET_PLANS_BY_OWNER = gql`
  query plansByOwner($ownerAppUserId: Int!) {
    plans(ownerAppUserId: $ownerAppUserId) {
      ...OwnerPlan
    }
  }
  ${OWNER_PLAN_DATA}
`

export const GET_PLAN_BOARD = gql`
  query plan($id: String!) {
    plan(id: $id) {
      ...boardPlan
    }
    planAlerts(planId: $id) {
      ...boardAlert
    }
  }
  ${BOARD_PLAN_DATA}
  ${BOARD_ALERT_DATA}
`

export const GET_PRIMARY_PLAN = gql`
  query primaryPlan($ownerAppUserId: Int!) {
    primaryPlan(ownerAppUserId: $ownerAppUserId) {
      ...boardPlan
    }
  }
  ${BOARD_PLAN_DATA}
`

export const GET_PLACEHOLDER_COURSE_DETAIL = gql`
  query placeholderCourseDetail(
    $courseNumber: String
    $externalCourseId: String
    $itemId: String!
    $subjectId: String
  ) {
    placeholderCourseDetail(
      courseNumber: $courseNumber
      externalCourseId: $externalCourseId
      itemId: $itemId
      subjectId: $subjectId
    ) {
      course {
        campus
        campusDescription
        career
        courseNumber
        creditsMax
        creditsMin
        description
        environment
        externalCourseId
        inProgress
        requirementTitles
        requisiteDescription
        requisiteDescriptionLong
        subjectDescription
        subjectId
        title
        typicallyOffered
        usedToMeetRequirement
      }
      alerts {
        ...boardAlert
      }
    }
  }
  ${BOARD_ALERT_DATA}
`

export const GET_PLAN_ITEM_DETAILS = gql`
  query planItem($id: String!, $planId: String!) {
    plan(id: $planId) {
      ...boardPlan
    }
    planItem(id: $id) {
      ...planItemDetails
    }
    planAlerts(planId: $planId, itemId: $id) {
      ...boardAlert
    }
  }
  ${PLAN_ITEM_DETAILS}
  ${BOARD_PLAN_DATA}
  ${BOARD_ALERT_DATA}
`

export const ADD_PLAN_MUTATION = gql`
  mutation AddPlan($input: CreatePlanInput!) {
    addPlan(input: $input) {
      ...OwnerPlan
      ...boardPlan
    }
  }
  ${OWNER_PLAN_DATA}
  ${BOARD_PLAN_DATA}
`

export const GET_PLAN_GROUP = gql`
  query groupById($id: String!) {
    planGroup(id: $id) {
      ...boardGroup
    }
  }
  ${BOARD_GROUP_DATA}
`

export const GET_PLAN_GROUPS = gql`
  query planGroups($planId: String!) {
    planGroups(planId: $planId) {
      ...boardGroup
    }
  }
  ${BOARD_GROUP_DATA}
`

export const GET_PLAN_UNREAD = gql`
  query planUnread($id: String!) {
    planUnread(id: $id)
  }
`

export const ADD_PLAN_GROUP_MUTATION = gql`
  mutation AddPlanGroup($input: CreatePlanGroupInput!) {
    addPlanGroup(input: $input) {
      ...boardGroup
    }
  }
  ${BOARD_GROUP_DATA}
`

export const UPDATE_PLAN_GROUP_TITLE_MUTATION = gql`
  mutation UpdatePlanGroupTitle($input: UpdatePlanGroupTitleInput!) {
    updatePlanGroupTitle(input: $input) {
      id
      title
    }
  }
`

export const ADD_TEMPLATE_GROUP_MUTATION = gql`
  mutation AddTemplateGroup($input: CreatePlanGroupInput!) {
    addTemplateGroup(input: $input) {
      ...boardGroup
    }
  }
  ${BOARD_GROUP_DATA}
`

export const UPDATE_TEMPLATE_GROUP_TITLE_MUTATION = gql`
  mutation UpdateTemplateGroupTitle($input: UpdateTemplateGroupTitleInput!) {
    updateTemplateGroupTitle(input: $input) {
      id
      ...boardGroup
    }
  }
  ${BOARD_GROUP_DATA}
`

export const MOVE_TEMPLATE_GROUP_MUTATION = gql`
  mutation MoveTemplateGroup($input: MovePlanGroupInput!) {
    moveTemplateGroup(input: $input) {
      id
      planId
      displaySequence
    }
  }
`

export const DELETE_TEMPLATE_GROUP_MUTATION = gql`
  mutation DeleteTemplateGroup($input: String!) {
    deleteTemplateGroup(input: $input) {
      id
      success
      message
    }
  }
`

export const ADD_PLAN_ITEM_MUTATION = gql`
  mutation AddPlanItem($input: CreatePlanItemInput!) {
    addPlanItem(input: $input) {
      ...boardItem
    }
  }
  ${BOARD_ITEM_DATA}
`

export const UPDATE_PLAN_ITEM_MUTATION = gql`
  mutation UpdatePlanItem($input: UpdatePlanItemInput!) {
    updatePlanItem(input: $input) {
      __typename
      id
      availability
      courseNumber
      creditsMax
      creditsMin
      description
      displaySequence
      equivalentCourseGroupId
      externalCourseId
      groupId
      inProgress
      isActive
      isCoursePlaceholder
      locked
      nonCourseCreditsMax
      nonCourseCreditsMin
      nonCourseDescription
      nonCourseTitle
      placeholderCourses {
        ...placeholderCourse
      }
      planId
      required
      requirementTitles
      requisiteDescription
      requisiteDescriptionLong
      showRequisiteAlert
      subjectId
      title
      typicallyOffered
      usedToMeetRequirement
    }
    ${PLACEHOLDER_COURSE_DATA}
  }
`

export const MOVE_PLAN_ITEM_MUTATION = gql`
  mutation MovePlanItem($input: MovePlanItemInput!) {
    movePlanItem(input: $input) {
      ...planItemDetails
    }
  }
  ${PLAN_ITEM_DETAILS}
`

export const DELETE_PLAN_ITEM_MUTATION = gql`
  mutation DeletePlanItem($input: String!) {
    deletePlanItem(input: $input) {
      id
      success
      message
    }
  }
`

export const COPY_PLAN_MUTATION = gql`
  mutation CopyPlan($input: String!) {
    copyPlan(input: $input) {
      ...OwnerPlan
    }
  }
  ${OWNER_PLAN_DATA}
`

export const COPY_EMBEDDED_PLAN_MUTATION = gql`
  mutation CopyEmbeddedPlan($input: String!) {
    copyEmbeddedPlan(input: $input) {
      ...OwnerPlan
    }
  }
  ${OWNER_PLAN_DATA}
`

export const MAKE_PLAN_PRIMARY_MUTATION = gql`
  mutation MakePlanPrimary($input: String!) {
    makePlanPrimary(input: $input) {
      id
      success
      message
    }
  }
`

export const UPDATE_PLAN_TITLE_MUTATION = gql`
  mutation UpdatePlanTitle($input: UpdatePlanTitleInput!) {
    updatePlanTitle(input: $input) {
      id
      title
    }
  }
`

export const UPDATE_LOCK_FOR_ALL_ITEMS_MUTATION = gql`
  mutation UpdateLockForAllItems($input: UpdateLockForAllItemsInput!) {
    updateLockForAllItems(input: $input) {
      success
      message
    }
  }
`

export const DELETE_PLAN_MUTATION = gql`
  mutation DeletePlan($input: String!) {
    deletePlan(input: $input) {
      id
      success
      message
    }
  }
`

export const CLEAR_PLAN_MUTATION = gql`
  mutation ClearPlan($input: String!) {
    clearPlan(input: $input) {
      ...boardPlan
    }
  }
`

export const DELETE_PLAN_GROUP_MUTATION = gql`
  mutation DeletePlanGroup($input: String!) {
    deletePlanGroup(input: $input) {
      id
      success
      message
    }
  }
`

export const MOVE_PLAN_GROUP_MUTATION = gql`
  mutation MovePlanGroup($input: MovePlanGroupInput!) {
    movePlanGroup(input: $input) {
      id
      planId
      displaySequence
    }
  }
`

export const GET_CHANGE_LOG = gql`
  query changes($planId: String!, $first: Int!, $after: String) {
    changes(planId: $planId, first: $first, after: $after) {
      ...changeLogConnection
    }
  }
  ${CHANGE_LOG_CONNECTION}
`

export const CHANGE_LOG_SUBSCRIPTION = gql`
  subscription onChangeLogAdded($planId: String!) {
    changeLogAdded(planId: $planId) {
      id
      planId
      itemId
      groupId
      description
      changedByAppUserId
      changedTimestamp
    }
  }
`

export const PLAN_CHANGED_BY_SUBSCRIPTION = gql`
  subscription onPlanChangedBy($planId: String!) {
    planChangedBy(planId: $planId) {
      changedByAppUserId
    }
  }
`

export const ADD_COMMENT_MUTATION = gql`
  mutation AddComment($input: CreateCommentInput!) {
    addComment(input: $input) {
      ...boardComment
    }
  }
  ${BOARD_COMMENT_DATA}
`

export const DELETE_COMMENT_MUTATION = gql`
  mutation DeleteComment($input: String!) {
    deleteComment(input: $input) {
      id
      success
      message
    }
  }
`

export const MARK_ALL_COMMENTS_READ_MUTATION = gql`
  mutation MarkAllCommentsAsRead($input: String!) {
    markAllCommentsAsRead(input: $input) {
      id
    }
  }
`

export const COMMENT_ADDED_SUBSCRIPTION = gql`
  subscription CommentAdded($planId: String, $subscribeToAll: Boolean) {
    commentAdded(planId: $planId, subscribeToAll: $subscribeToAll) {
      ...boardComment
    }
  }
`

export const GET_COMMENTS_BY_GROUPID = gql`
  query commentsByGroupId($groupId: String!) {
    comments(groupId: $groupId) {
      ...boardComment
    }
  }
`

export const UPDATE_PLAN_GROUP_TERM_MUTATION = gql`
  mutation updatePlanGroupTerm($input: UpdatePlanGroupTermInput!) {
    updatePlanGroupTerm(input: $input) {
      id
      externalTermId
      title
    }
  }
`

export const TEMPLATE_DATA = gql`
  fragment Template on Plan {
    id
    title
    createdByAppUserId
    creatorName
    campusCode
    campusDescription
    catalogYear
    catalogYearDescription
    collegeCode
    collegeDescription
    concentrationCode
    concentrationDescription
    degreeCode
    degreeDescription
    majors {
      ...boardMajor
    }
    minors {
      ...boardMinor
    }
    publishedAt
    status
  }
  ${BOARD_MAJOR_DATA}
  ${BOARD_MINOR_DATA}
`

export const GET_TEMPLATES = gql`
  query templates($environment: String) {
    templates(environment: $environment) {
      ...Template
    }
  }
  ${TEMPLATE_DATA}
`

export const GET_TEMPLATE = gql`
  query template($id: String!) {
    template(id: $id) {
      ...boardPlan
    }
    planAlerts(planId: $id) {
      ...boardAlert
    }
  }
`

/** Template Specific */
export const UPDATE_TEMPLATE_CAMPUS_MUTATION = gql`
  mutation UpdateTemplateCampus($input: UpdatePlanInput!) {
    updateTemplate(input: $input) {
      id
      campusCode
      campusDescription
    }
  }
`

export const UPDATE_TEMPLATE_CATALOG_MUTATION = gql`
  mutation UpdateTemplateCatalog($input: UpdatePlanInput!) {
    updateTemplate(input: $input) {
      id
      catalogYear
      catalogYearDescription
    }
  }
`

export const UPDATE_TEMPLATE_COLLEGE_MUTATION = gql`
  mutation UpdateTemplateCollege($input: UpdatePlanInput!) {
    updateTemplate(input: $input) {
      id
      collegeCode
      collegeDescription
    }
  }
`

export const UPDATE_TEMPLATE_CONCENTRATION_MUTATION = gql`
  mutation UpdateTemplateConcentration($input: UpdatePlanInput!) {
    updateTemplate(input: $input) {
      id
      concentrationCode
      concentrationDescription
    }
  }
`

export const UPDATE_TEMPLATE_DESCRIPTION_MUTATION = gql`
  mutation UpdateTemplateDescription($input: UpdatePlanInput!) {
    updateTemplate(input: $input) {
      id
      description
    }
  }
`

export const UPDATE_TEMPLATE_DEGREE_MUTATION = gql`
  mutation UpdateTemplateDegree($input: UpdatePlanInput!) {
    updateTemplate(input: $input) {
      id
      degreeCode
      degreeDescription
    }
  }
`

export const GET_ACADEMIC_STRUCTURE_DATA = gql`
  query academicStructureData {
    campuses {
      id
      code
      description
    }
    getColleges {
      id
      code
      description
    }
    getDegrees {
      id
      code
      description
    }
    getMajors {
      id
      code
      description
    }
    getMinors {
      id
      code
      description
      major
    }
    getConcentrations {
      id
      code
      description
      major
      minor
    }
  }
`

export const ADD_TEMPLATE_MAJOR_MUTATION = gql`
  mutation AddMajor($input: CreatePlanMajorInput!) {
    addMajor(input: $input) {
      ...boardMajor
    }
  }
  ${BOARD_MAJOR_DATA}
`

export const DELETE_TEMPLATE_MAJOR_MUTATION = gql`
  mutation DeleteMajor($id: String!) {
    deleteMajor(id: $id) {
      id
      success
      message
    }
  }
`

export const ADD_TEMPLATE_MINOR_MUTATION = gql`
  mutation AddMinor($input: CreatePlanMinorInput!) {
    addMinor(input: $input) {
      ...boardMinor
    }
  }
  ${BOARD_MINOR_DATA}
`

export const DELETE_TEMPLATE_MINOR_MUTATION = gql`
  mutation DeleteMinor($id: String!) {
    deleteMinor(id: $id) {
      id
      success
      message
    }
  }
`

export const ADD_TEMPLATE_MUTATION = gql`
  mutation AddTemplate($input: CreatePlanInput!) {
    addTemplate(input: $input) {
      ...OwnerPlan
      ...boardPlan
    }
  }
`

export const PUBLISH_TEMPLATE_MUTATION = gql`
  mutation PublishTemplate($input: String!) {
    publishTemplate(input: $input) {
      ...boardPlan
    }
  }
`

export const ARCHIVE_TEMPLATE_MUTATION = gql`
  mutation ArchiveTemplate($input: String!) {
    archiveTemplate(input: $input) {
      ...boardPlan
    }
  }
`

export const DELETE_TEMPLATE_MUTATION = gql`
  mutation DeleteTemplate($id: String!) {
    deleteTemplate(id: $id) {
      id
      success
      message
    }
  }
`

export const COPY_ALL_PUBLISHED_TEMPLATES_MUTATION = gql`
  mutation CopyAllPublishedTemplates {
    copyAllPublishedTemplates {
      copiedCount
      success
      message
      plans {
        id
        title
        status
      }
    }
  }
`

export const BULK_LOCK_ALL_STUDENT_PLANS_MUTATION = gql`
  mutation BulkLockAllStudentPlans($locked: Boolean!) {
    bulkLockAllStudentPlans(locked: $locked) {
      jobId
      message
    }
  }
`

export const BULK_LOCK_JOB_STATUS_QUERY = gql`
  query BulkLockJobStatus($jobId: String!) {
    bulkLockJobStatus(jobId: $jobId) {
      jobId
      status
      totalStudents
      processedStudents
      lockedPlans
      errors
      startedAt
      completedAt
    }
  }
`

export const APPLY_TEMPLATE_MUTATION = gql`
  mutation ApplyTemplate($input: ApplyTemplateInput!) {
    applyTemplate(input: $input) {
      correlationId
      studentCount
      templateTitle
    }
  }
`

export const APPLY_TEMPLATE_RESPONSE = gql`
  query applyTemplateResponse($correlationId: String!) {
    applyTemplateResponse(correlationId: $correlationId) {
      plansCreated {
        ...boardPlan
      }
      correlationId
      status
      studentCount
      templateTitle
      failureReason
    }
  }
`

/**
 * Notifies when the apply template process is complete.
 * Matches based on correlationId
 */
export const APPLY_TEMPLATE_COMPLETE_SUBSCRIPTION = gql`
  subscription applyTemplateComplete($correlationIds: [String!]!) {
    applyTemplateComplete(correlationIds: $correlationIds) {
      plansCreated {
        ...boardPlan
      }
      correlationId
      status
      studentCount
      templateTitle
      failureReason
    }
  }
`

export const TOUCH_CHANGELOG_VIEWED = gql`
  mutation touchChangelogViewed($input: String!) {
    touchChangelogViewed(input: $input) {
      id
      changelogViewedAt
    }
  }
`

export const GET_CHANGELOG_VIEWED = gql`
  query changeLogViewedAt($planId: String!) {
    plan(id: $planId) {
      id
      changelogViewedAt
    }
  }
`

export const DISMISS_ALERT = gql`
  mutation dismissPlanAlert($input: DismissAlertInput!) {
    dismissPlanAlert(input: $input) {
      id
      success
      message
    }
  }
`

export const GET_PLAN_ALERTS = gql`
  query planAlerts($planId: String!, $groupId: String, $itemId: String) {
    planAlerts(planId: $planId, groupId: $groupId, itemId: $itemId) {
      ...boardAlert
    }
  }
  ${BOARD_ALERT_DATA}
`

/** Placeholder Template Specific */
export const PLACEHOLDER_TEMPLATES = gql`
  query placeholderTemplates($environment: String) {
    placeholderTemplates(environment: $environment) {
      ...planItemDetails
    }
  }
`
export const PLACEHOLDER_TEMPLATE = gql`
  query placeholderTemplate($id: String!) {
    placeholderTemplate(id: $id) {
      ...planItemDetails
    }
  }
`

export const PLACEHOLDER_TEMPLATE_IN_USE_LOCATIONS = gql`
  query placeholderTemplateInUseLocations($id: String!) {
    placeholderTemplateInUseLocations(id: $id) {
      ...Template
    }
  }
  ${TEMPLATE_DATA}
`

export const ADD_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation AddPlaceholderTemplate {
    addPlaceholderTemplate {
      ...planItemDetails
    }
  }
`

export const DELETE_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation DeletePlaceholderTemplate($input: String!) {
    deletePlaceholderTemplate(input: $input) {
      id
      success
      message
    }
  }
`

export const ARCHIVE_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation ArchivePlaceholderTemplate($input: String!) {
    archivePlaceholderTemplate(input: $input) {
      ...planItemDetails
    }
  }
`

export const PUBLISH_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation publishPlaceholderTemplate($input: String!) {
    publishPlaceholderTemplate(input: $input) {
      ...planItemDetails
    }
  }
`

export const COPY_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation CopyPlaceholderTemplate($input: String!) {
    copyPlaceholderTemplate(input: $input) {
      ...planItemDetails
    }
  }
`

export const UPDATE_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation UpdatePlaceholderTemplate($input: UpdatePlanItemInput!) {
    updatePlaceholderTemplate(input: $input) {
      ...planItemDetails
    }
  }
`

export const ADD_PLACEHOLDER_COURSE = gql`
  mutation AddPlaceholderCourse($input: PlaceholderCourseInput!) {
    addPlaceholderCourse(input: $input) {
      id
      ...placeholderCourse
    }
  }
`

export const DELETE_PLACEHOLDER_COURSE = gql`
  mutation DeletePlaceholderCourse($id: String!) {
    deletePlaceholderCourse(id: $id) {
      id
      success
      message
    }
  }
`

export const SEARCH_PLACEHOLDER_TEMPLATES = gql`
  query searchPlaceholderTemplates(
    $searchText: String!
    $environment: String!
    $campusCode: String
  ) {
    searchPlaceholderTemplates(
      searchText: $searchText
      environment: $environment
      campusCode: $campusCode
    ) {
      ...boardItem
    }
  }
  ${BOARD_ITEM_DATA}
`

export const ITEM_FROM_PLACEHOLDER_TEMPLATE_MUTATION = gql`
  mutation itemFromPlaceholderTemplate($input: ItemFromPlaceholderTemplateInput!) {
    itemFromPlaceholderTemplate(input: $input) {
      ...boardItem
    }
  }
  ${BOARD_ITEM_DATA}
`

export const ADD_PLACEHOLDER_TEMPLATE_MAJOR_MUTATION = gql`
  mutation AddItemMajor($input: CreateItemMajorInput!) {
    addItemMajor(input: $input) {
      ...itemMajor
    }
  }
  ${ITEM_MAJOR_DATA}
`

export const DELETE_PLACEHOLDER_TEMPLATE_MAJOR_MUTATION = gql`
  mutation DeleteItemMajor($id: String!) {
    deleteItemMajor(id: $id) {
      id
      success
      message
    }
  }
`

export const ADD_PLACEHOLDER_TEMPLATE_MINOR_MUTATION = gql`
  mutation AddItemMinor($input: CreateItemMinorInput!) {
    addItemMinor(input: $input) {
      ...itemMinor
    }
  }
  ${ITEM_MINOR_DATA}
`

export const DELETE_PLACEHOLDER_TEMPLATE_MINOR_MUTATION = gql`
  mutation DeleteItemMinor($id: String!) {
    deleteItemMinor(id: $id) {
      id
      success
      message
    }
  }
`

// --- Read-Only for Students ---

export const UPDATE_PLAN_READ_ONLY_FOR_STUDENTS_MUTATION = gql`
  mutation UpdatePlanReadOnlyForStudents($planId: String!, $readOnlyForStudents: Boolean!) {
    updatePlanReadOnlyForStudents(planId: $planId, readOnlyForStudents: $readOnlyForStudents) {
      id
      readOnlyForStudents
    }
  }
`

export const RESET_ALL_PLANS_READ_ONLY_MUTATION = gql`
  mutation ResetAllPlansReadOnly($readOnlyForStudents: Boolean!) {
    resetAllPlansReadOnly(readOnlyForStudents: $readOnlyForStudents) {
      jobId
      message
    }
  }
`

export const READ_ONLY_BULK_ACTION_STATUS_QUERY = gql`
  query ReadOnlyBulkActionStatus($jobId: String!) {
    readOnlyBulkActionStatus(jobId: $jobId) {
      jobId
      status
      totalPlans
      processedPlans
      failedPlans
      errors
      startedAt
      completedAt
    }
  }
`
